import React, { useEffect, useState } from 'react';
import { ArrowLeft, TrendingUp, Edit, Trash2, X, Search, Lock, History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import LogoLoader from '../components/LogoLoader';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const ALLOWED_EMAIL = 'alameengoldandsilver@gmail.com';

const s = {
  page: { height: '100vh', background: '#f9f5e8', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  navbar: {
    background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
    padding: '0 20px', height: '64px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
    flexShrink: 0,
  },
  navBrand: { display: 'flex', alignItems: 'center', gap: '10px' },
  navCoin: {
    width: '42px', height: '42px',
    borderRadius: '50%', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  navTitle: { color: '#fff', fontSize: '18px', fontWeight: '800', margin: 0 },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.1)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '8px 14px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  content: {  padding: '32px 24px' },
  headerCard: {
    background: '#fff', borderRadius: '16px', padding: '20px 28px',
    marginBottom: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', gap: '14px',
  },
  iconBox: {
    width: '46px', height: '46px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    borderRadius: '12px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: '#fff', flexShrink: 0,
  },
  headerTitle: { fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 },
  headerSub: { fontSize: '13px', color: '#64748b', margin: '3px 0 0' },
  tableCard: { background: '#fff', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', letterSpacing: '0.5px', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap' },
  td: { padding: '8px 14px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155', whiteSpace: 'nowrap', verticalAlign: 'middle' },
  badge: { display: 'inline-block', background: '#fdf5dc', color: '#7A5C1E', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' },
  todayBadge: { display: 'inline-block', background: '#dcfce7', color: '#16a34a', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: '700', marginLeft: '8px' },
  inputStyle: {
    width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0',
    borderRadius: '9px', fontSize: '14px', background: '#f8fafc',
    boxSizing: 'border-box', outline: 'none', marginBottom: '14px',
  },
  saveBtn: {
    width: '100%', padding: '12px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    color: '#fff', border: 'none', borderRadius: '9px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
  },
};

const getLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function MetalRates() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyRate, setHistoryRate] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const today = getLocalDate();
  const { adminName } = useAuth();

  const fetchRates = async () => {
    const { data } = await supabase
      .from('metal_rates')
      .select('*')
      .order('date', { ascending: false });
    setRates(data || []);
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email?.toLowerCase() !== ALLOWED_EMAIL) {
        setAllowed(false);
        setLoading(false);
      } else {
        setAllowed(true);
        fetchRates();
      }
    });
  }, []);

  const fetchHistory = async (rate) => {
    setHistoryRate(rate);
    setHistoryLoading(true);
    const { data } = await supabase
      .from('metal_rate_history')
      .select('*')
      .eq('metal_rate_id', rate.id)
      .order('updated_at', { ascending: false });
    setHistoryData(data || []);
    setHistoryLoading(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    setSaving(true);
    // Save current values as history before updating
    await supabase.from('metal_rate_history').insert([{
      metal_rate_id: editingRate.id,
      date: editingRate.date,
      old_gold_rate: Number(editingRate.gold_rate),
      old_silver_rate: Number(editingRate.silver_rate),
      new_gold_rate: Number(form.get('gold_rate')),
      new_silver_rate: Number(form.get('silver_rate')),
      updated_by: adminName,
    }]);
    const { error } = await supabase
      .from('metal_rates')
      .update({
        gold_rate: Number(form.get('gold_rate')),
        silver_rate: Number(form.get('silver_rate')),
        added_by: adminName,
      })
      .eq('id', editingRate.id);
    setSaving(false);
    if (!error) {
      toast.success('Rate updated successfully!');
      setEditingRate(null);
      fetchRates();
    } else {
      toast.error(error.message);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    const { error } = await supabase.from('metal_rates').delete().eq('id', deleteTarget.id);
    setIsDeleting(false);
    setDeleteTarget(null);
    if (!error) {
      toast.success('Rate deleted!');
      fetchRates();
    } else {
      toast.error(error.message);
    }
  };

  if (allowed === null || loading) return <LogoLoader />;

  if (allowed === false) return (
    <div style={{ minHeight: '100vh', background: '#f9f5e8', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <nav style={s.navbar}>
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%' }} />
          </div>
          <p style={s.navTitle}>Al Ameen Gold &amp; Silver</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={s.backBtn}>
          <ArrowLeft size={15} /> Back
        </button>
      </nav>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '48px 40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Lock size={34} color="#ef4444" />
          </div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>Access Denied</h2>
          <p style={{ margin: '0 0 6px', fontSize: '15px', color: '#64748b' }}>You don't have permission to view Metal Rates.</p>
          <p style={{ margin: '0 0 28px', fontSize: '13px', color: '#94a3b8' }}>This page is restricted to authorised administrators only.</p>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            <ArrowLeft size={16} /> Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const filteredRates = rates.filter(r => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    const dateStr = new Date(r.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toLowerCase();
    return (
      dateStr.includes(q) ||
      String(r.gold_rate).includes(q) ||
      String(r.silver_rate).includes(q) ||
      (r.added_by || '').toLowerCase().includes(q)
    );
  });

  return (
    <div style={s.page}>
      {/* History Popup — Timeline */}
      {historyRate && (
        <div className="gs-hist-overlay">
          <div className="gs-hist-box">

            {/* ── Header ── */}
            <div className="gs-hist-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg,#C9A84C,#9A7A2C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <History size={18} color="#fff" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>Rate History</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    {new Date(historyRate.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <button onClick={() => setHistoryRate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '6px', display: 'flex', flexShrink: 0, borderRadius: '8px' }}>
                <X size={20} />
              </button>
            </div>

            {/* ── Current live values ── */}
            <div className="gs-hist-cur">
              <div className="gs-hist-cur-gold">
                <div className="gs-hist-cur-label" style={{ color: '#92400e' }}>🥇 Current Gold</div>
                <div className="gs-hist-cur-val" style={{ color: '#9A7A2C' }}>₹{Number(historyRate.gold_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
              <div className="gs-hist-cur-silver">
                <div className="gs-hist-cur-label" style={{ color: '#475569' }}>🥈 Current Silver</div>
                <div className="gs-hist-cur-val" style={{ color: '#475569' }}>₹{Number(historyRate.silver_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>

            {/* ── Timeline list ── */}
            <div className="gs-hist-list">
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' }}>Loading…</div>
              ) : historyData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📋</div>
                  <p style={{ margin: 0, fontSize: '14px', color: '#94a3b8', fontWeight: '600' }}>No update history yet.</p>
                  <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#cbd5e1' }}>Each edit will be recorded here.</p>
                </div>
              ) : (
                <div className="gs-tl">
                  {historyData.map((h, i) => (
                    <div key={h.id} className="gs-tl-item">
                      {/* Left: dot + line */}
                      <div className="gs-tl-left">
                        <div className={`gs-tl-dot${i === 0 ? ' gs-tl-dot--latest' : ''}`}>
                          {i === 0
                            ? <span style={{ fontSize: '11px' }}>★</span>
                            : <span style={{ fontSize: '10px', color: '#94a3b8' }}>{historyData.length - i}</span>
                          }
                        </div>
                        {i < historyData.length - 1 && <div className="gs-tl-line" />}
                      </div>

                      {/* Right: content card */}
                      <div className={`gs-tl-card${i === 0 ? ' gs-tl-card--latest' : ''}`}>
                        {/* Who + when */}
                        <div className="gs-tl-meta">
                          <div className="gs-tl-who">
                            <div className="gs-tl-avatar">
                              {h.updated_by ? h.updated_by.charAt(0).toUpperCase() : 'A'}
                            </div>
                            <div className="gs-tl-info">
                              <span className="gs-tl-name" title={h.updated_by || 'Admin'}>{h.updated_by || 'Admin'}</span>
                              {i === 0 && <span className="gs-hist-badge">Latest</span>}
                            </div>
                          </div>
                          <span className="gs-tl-time">
                            {h.updated_at
                              ? new Date(h.updated_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
                              : '—'}
                          </span>
                        </div>

                        {/* Rate changes */}
                        <div className="gs-hist-rates">
                          <div className="gs-hist-rate-gold">
                            <div className="gs-hist-rate-lbl" style={{ color: '#92400e' }}>🥇 Gold</div>
                            <div className="gs-hist-rate-row">
                              <span className="gs-hist-old">₹{Number(h.old_gold_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              <span className="gs-hist-arrow">→</span>
                              <span className="gs-hist-new" style={{ color: '#9A7A2C' }}>₹{Number(h.new_gold_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                          <div className="gs-hist-rate-silver">
                            <div className="gs-hist-rate-lbl" style={{ color: '#475569' }}>🥈 Silver</div>
                            <div className="gs-hist-rate-row">
                              <span className="gs-hist-old">₹{Number(h.old_silver_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              <span className="gs-hist-arrow">→</span>
                              <span className="gs-hist-new" style={{ color: '#475569' }}>₹{Number(h.new_silver_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}
      {/* Edit Rate Modal */}
      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '28px',
            width: '100%', maxWidth: '380px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)', textAlign: 'center',
          }}>
            <div style={{
              width: '60px', height: '60px', borderRadius: '50%',
              background: 'rgba(239,68,68,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Trash2 size={24} color="#ef4444" />
            </div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Delete Rate?</h2>
            <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '700', color: '#9A7A2C' }}>
              {new Date(deleteTarget.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
            <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#64748b' }}>
              Gold: ₹{Number(deleteTarget.gold_rate).toLocaleString('en-IN')} &nbsp;|&nbsp; Silver: ₹{Number(deleteTarget.silver_rate).toLocaleString('en-IN')}
            </p>
            <button
              onClick={handleDeleteConfirmed}
              disabled={isDeleting}
              style={{
                width: '100%', padding: '12px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff', border: 'none', borderRadius: '9px',
                fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                opacity: isDeleting ? 0.7 : 1, marginBottom: '8px',
              }}
            >
              {isDeleting ? 'Deleting…' : 'Yes, Delete'}
            </button>
            <button
              onClick={() => setDeleteTarget(null)}
              style={{
                width: '100%', padding: '12px',
                background: '#f1f5f9', color: '#475569', border: 'none',
                borderRadius: '9px', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingRate && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '28px',
            width: '100%', maxWidth: '400px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Edit Metal Rate</h2>
              <button onClick={() => setEditingRate(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '13px', color: '#64748b' }}>
              Date: <strong style={{ color: '#0f172a' }}>
                {new Date(editingRate.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </strong>
              {editingRate.date === today && <span style={{ ...s.todayBadge, marginLeft: '8px' }}>Today</span>}
            </p>
            <form onSubmit={handleUpdate}>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>Gold Rate (₹/g)</label>
              <input
                name="gold_rate" type="number" step="0.01" min="0"
                defaultValue={editingRate.gold_rate} required
                style={{ ...s.inputStyle, borderColor: '#C9A84C' }}
              />
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>Silver Rate (₹/g)</label>
              <input
                name="silver_rate" type="number" step="0.01" min="0"
                defaultValue={editingRate.silver_rate} required
                style={{ ...s.inputStyle, borderColor: '#94a3b8' }}
              />
              <button type="submit" disabled={saving} style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : 'Update Rate'}
              </button>
            </form>
          </div>
        </div>
      )}
      <nav style={s.navbar} className="gs-navbar">
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} className="gs-nav-coin" onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <p style={s.navTitle} className="gs-nav-title">Al Ameen Gold & Silver</p>
        </div>
        <button onClick={() => navigate('/admin')} style={s.backBtn}>
          <ArrowLeft size={15} /> Back
        </button>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={s.content} className="gs-content">
        <div style={s.headerCard} className="gs-header-card gs-mr-header">
          <div style={s.iconBox}><TrendingUp size={22} /></div>
          <div>
            <p style={s.headerTitle}>Metal Rate History</p>
            {/* <p style={s.headerSub}>{rates.length} {rates.length === 1 ? 'entry' : 'entries'} — Daily Gold & Silver rates</p> */}
          </div>
        </div>

        <div style={{ position: 'relative', marginBottom: '16px' }} className="gs-mr-search">
          <Search size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ padding: '10px 14px 10px 40px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#334155', background: '#fff', outline: 'none', boxSizing: 'border-box', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
          />
          {searchText && (
            <button onClick={() => setSearchText('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center' }}><X size={15} /></button>
          )}
        </div>

        <div style={s.tableCard} className="gs-mr-table">
          <div className="gs-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '520px' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {/* <th style={s.th}>#</th> */}
                  <th style={s.th}>Date</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Gold Rate (₹/g)</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Silver Rate (₹/g)</th>
                  <th style={s.th}>Added By</th>
                  <th style={s.th}>Added At</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRates.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>
                      No rate entries found.
                    </td>
                  </tr>
                )}
                {/* colSpan fix for 6 cols */}
                {filteredRates.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    {/* <td style={s.td}><span style={s.badge}>#{i + 1}</span></td> */}
                    <td style={{ ...s.td, fontWeight: '700', color: '#0f172a' }}>
                      {new Date(r.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      {r.date === today && <span style={s.todayBadge}>Today</span>}
                    </td>
                    <td style={{ ...s.td, fontWeight: '700', color: '#9A7A2C', textAlign: 'center' }}>
                      ₹{Number(r.gold_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ ...s.td, fontWeight: '700', color: '#64748b', textAlign: 'center' }}>
                      ₹{Number(r.silver_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={{ ...s.td, fontSize: '13px', color: '#0f172a', fontWeight: '600' }}>
                      {r.added_by || '—'}
                    </td>
                    <td style={{ ...s.td, fontSize: '12px', color: '#94a3b8' }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          title="View History"
                          onClick={() => fetchHistory(r)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C9A84C', padding: '6px', borderRadius: '6px' }}
                        >
                          <History size={16} />
                        </button>
                        <button
                          title="Edit Rate"
                          onClick={() => setEditingRate(r)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: '6px', borderRadius: '6px' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          title="Delete Rate"
                          onClick={() => setDeleteTarget(r)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '6px', borderRadius: '6px' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
            <span>{filteredRates.length} {filteredRates.length === 1 ? 'Record' : 'Records'} shown</span>
            {searchText && <span style={{ color: '#C9A84C', fontWeight: '700' }}>⚡ Filters applied</span>}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
